import { Search, XCircle, ScanLine } from 'lucide-react';
import React from 'react';

/**
 * SearchInput - Universal search component for the system.
 * Supports both clean "Dashboard" look and high-contrast "Industrial" look.
 * 
 * @param {Object} props
 * @param {string} props.value - Current input value
 * @param {function} props.onChange - Handler for value changes
 * @param {string} [props.placeholder="Search..."]
 * @param {string} [props.label] - Optional top label
 * @param {boolean} [props.autoFocus=false]
 * @param {string} [props.variant="default"] - "default" | "industrial"
 * @param {boolean} [props.showStatus=false] - Show "Active/Awaiting" indicator (industrial only)
 */
const SearchInput = ({
    value,
    onChange,
    placeholder = "Search...",
    label,
    autoFocus = false,
    variant = "default",
    showStatus = false,
    colorMode = 'amber'
}) => {
    const isIndustrial = variant === 'industrial';
    const isEmerald = colorMode === 'emerald';


    if (isIndustrial) {
        return (
            <div className="flex flex-col gap-2 w-full pb-2">
                {/* Header / Meta / Status Indicator */}
                <div className="flex items-center justify-between ml-1 px-1">
                    {label && (
                        <label className={`text-[10px] font-black uppercase tracking-[0.3em] flex items-center gap-1.5 focus-within:animate-pulse ${isEmerald ? 'text-emerald-500' : 'text-accent'}`}>
                            <ScanLine size={14} className={isEmerald ? 'text-emerald-500' : 'text-accent'} />
                            {label}
                        </label>
                    )}
                    {showStatus && (
                        <div className="flex bg-slate-200/50 px-2.5 py-1 rounded-full items-center gap-1.5">
                            <div className={`w-1.5 h-1.5 rounded-full ${value ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`} />
                            <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">{value ? 'Search Active' : 'Waiting for Input'}</span>
                        </div>
                    )}
                </div>

                {/* Industrial Search Bar */}
                <div className={`relative group rounded-2xl transition-all shadow-sm hover:shadow-md focus-within:shadow-2xl ${isEmerald ? 'focus-within:shadow-emerald-500/10' : 'focus-within:shadow-accent/10'} focus-within:-translate-y-0.5`}>
                    <div className={`absolute left-4 top-1/2 -translate-y-1/2 flex items-center justify-center p-2.5 rounded-xl transition-colors ${isEmerald ? 'bg-slate-100/80 group-focus-within:bg-emerald-500/10' : 'bg-slate-100/80 group-focus-within:bg-accent/10'}`}>
                        <Search className={`text-slate-400 transition-colors ${isEmerald ? 'group-focus-within:text-emerald-500' : 'group-focus-within:text-accent'}`} size={20} />
                    </div>

                    <input
                        id="inventoryGlobalSearch"

                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        onKeyDown={(e) => {
                            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'c') {
                                e.preventDefault();
                                onChange('');
                            }
                        }}
                        placeholder={placeholder}
                        className={`w-full pl-20 pr-16 py-5 bg-white border-2 border-slate-200 rounded-2xl outline-none font-bold text-slate-800 text-xl placeholder:text-slate-400/80 placeholder:font-medium transition-all hover:border-slate-300 font-sans ${isEmerald ? 'focus:border-emerald-500' : 'focus:border-accent'}`}
                    />

                    {value && (
                        <button
                            tabIndex={-1}
                            onClick={() => onChange('')}
                            className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-300 hover:text-red-500 transition-colors active:scale-90"
                            title="Clear query"
                        >
                            <XCircle size={22} />
                        </button>
                    )}
                </div>
            </div>
        );
    }

    // Default / Dashboard Variant
    return (
        <div className="flex flex-col gap- w-full">
            {label && (
                <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 flex items-center gap-2 px-1">
                    <Search size={12} strokeWidth={3} /> {label}
                </label>
            )}
            <div className="relative group">
                <input
                    id="inventoryGlobalSearch"
                    type="text"

                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onKeyDown={(e) => {
                        if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'c') {
                            e.preventDefault();
                            onChange('');
                        }
                    }}
                    placeholder={placeholder}
                    className={`w-full h-12 md:h-14 pl-12 pr-12 bg-white border border-slate-200 rounded-2xl outline-none transition-all font-bold text-slate-700 placeholder:text-slate-300 text-sm shadow-sm group-hover:shadow-md ${isEmerald ? 'focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/5' : 'focus:border-accent focus:ring-4 focus:ring-accent/5'}`}
                />
                <Search
                    className={`absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:transition-colors ${isEmerald ? 'group-focus-within:text-emerald-500' : 'group-focus-within:text-accent'}`}
                    size={18}
                    strokeWidth={3}
                />
                {value && (
                    <button
                        tabIndex={-1}
                        onClick={() => onChange('')}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 hover:text-red-500 transition-all active:scale-90"
                    >
                        <XCircle size={18} />
                    </button>
                )}
            </div>
        </div>
    );
};

export default SearchInput;

